// The commissioner's sheets for a seat past the draft (MANAGERS.md, J4 and
// J3): remove the manager of a team, send the link that hands a team nobody
// runs to a new one, put a bot on such a team or take it off, and nudge a
// manager about a lineup. Each says what will happen before it is done, and
// each reads its seat from the view by id, so a sheet left open across a
// refresh shows the seat as it is now rather than as it was when it opened.

import { useState } from 'react';
import type { LeagueLineupsView, LeagueSetupView, LineupGlance, SeatRef } from '@fsp/domain';
import { useDelight } from '../../delight/useDelight';
import { messageOf } from '../../data/api';
import { nudgeLineup, seatAct } from '../../data/leagueSetup';
import { Hint, Sheet } from '../../kit/overlays';
import { Button, Callout, Line, Stack, Text, TextField, tell } from '../../kit/ui';
import { Confirm } from './Confirm';
import { BotSeatSheet } from './BotChooser';

/** What removing a team's manager does, before it is done. */
export function removeConsequences(seat: SeatRef): string[] {
  const who = seat.ownerName ?? 'The manager';
  // A SHARED TEAM PASSES TO ITS CO-MANAGER (B-436): nothing else changes.
  const heir = seat.coManagers[0] ?? null;
  if (heir !== null)
    return [
      `${who} stops being a member of the league, and is told.`,
      `${heir}, who co-manages ${seat.name}, takes it over, with its roster, its record, its waiver claims and its trade offers.`,
      'The commissioner log says you did it.',
    ];
  return [
    `${who} stops being a member of the league, and is told.`,
    `${seat.name} stays, with its roster, its record and its name, and reads Former manager until somebody takes it over.`,
    'Until then it plays its last lineup each week, and nobody makes its moves.',
    `${who}’s waiver claims are withdrawn, and trade offers to or from the team that nobody has accepted are void. A trade already accepted goes ahead.`,
    'The commissioner log says you did it.',
  ];
}

/** What a team's link does, said beside it. */
export function inviteLines(seat: SeatRef): string[] {
  return [
    `Send this to whoever is taking over ${seat.name}. When they open it and sign in, the team is theirs as it stands, with its roster and its record, and they can rename it.`,
    ...(seat.seat === 'bot' ? [`${seat.botName ?? 'The bot'} stops playing it then.`] : []),
    'The link works once. A new link, or canceling this one, stops it working.',
  ];
}

const reached = 'Could not reach the site, so this may not have happened. Look again.';

export function RemoveManagerSheet({
  view,
  leagueId,
  teamId,
  onClose,
  onRemoved,
  onView,
}: {
  view: LeagueSetupView;
  leagueId: string;
  teamId: string | null;
  onClose: () => void;
  /** Straight on to the team's link: one flow, not two errands. */
  onRemoved: (teamId: string) => void;
  /** The seats as the act left them, before the screen's own read catches up. */
  onView: (view: LeagueSetupView) => void;
}) {
  const seat = view.seats.find((s) => s.teamId === teamId) ?? null;
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const close = () => {
    setProblem(null);
    onClose();
  };
  const remove = async () => {
    if (seat === null) return;
    setBusy(true);
    setProblem(null);
    const reply = await seatAct(leagueId, seat.teamId, 'remove');
    setBusy(false);
    if (reply.status !== 'ok') return setProblem(messageOf(reply, 'That did not work.', reached));
    onView(reply.data);
    const heir = seat.coManagers[0] ?? null;
    if (heir !== null) {
      // Handed over: no team waits for a new manager, so no link to send.
      tell(`${seat.ownerName ?? 'The manager'} is out. ${heir} runs ${seat.name} now.`, 'good');
      onClose();
      return;
    }
    tell(`${seat.ownerName ?? 'The manager'} is out. ${seat.name} stands.`, 'good');
    onRemoved(seat.teamId);
  };
  // The seat as it is now: somebody else may have opened it in the meantime.
  const removable = seat !== null && seat.seat === 'manager';
  return (
    <Sheet
      opened={teamId !== null}
      onClose={close}
      title={seat?.ownerName ? `Remove ${seat.ownerName}` : 'Remove a manager'}
    >
      {seat !== null && removable ? (
        <Stack gap={3}>
          <Callout>
            <Stack gap={1}>
              {removeConsequences(seat).map((line) => (
                <Text key={line} block>
                  {line}
                </Text>
              ))}
            </Stack>
          </Callout>
          {problem !== null ? <Callout tone="bad">{problem}</Callout> : null}
          <Line wrap>
            <Button variant="danger" onClick={() => void remove()} loading={busy} disabled={busy}>
              Remove {seat.ownerName}
            </Button>
            <Button variant="quiet" onClick={close} disabled={busy}>
              Keep them
            </Button>
          </Line>
        </Stack>
      ) : (
        <Text tone="secondary" block>
          Nobody is in that seat now.
        </Text>
      )}
    </Sheet>
  );
}

export function SeatInviteSheet({
  view,
  leagueId,
  teamId,
  onClose,
  onView,
}: {
  view: LeagueSetupView;
  leagueId: string;
  teamId: string | null;
  onClose: () => void;
  onView: (view: LeagueSetupView) => void;
}) {
  const seat = view.seats.find((s) => s.teamId === teamId) ?? null;
  const { play } = useDelight();
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const close = () => {
    setProblem(null);
    setCopied(false);
    onClose();
  };
  const run = async (act: 'invite' | 'cancel-invite', done: string) => {
    if (seat === null) return;
    setBusy(true);
    setProblem(null);
    setCopied(false);
    const reply = await seatAct(leagueId, seat.teamId, act);
    setBusy(false);
    if (reply.status !== 'ok') return setProblem(messageOf(reply, 'That did not work.', reached));
    onView(reply.data);
    tell(done, 'good');
  };

  const link = seat?.invite != null ? `${window.location.origin}/join/${seat.invite.code}` : null;
  const byHand = () => {
    document.querySelector<HTMLInputElement>('input[aria-label="Team invite link"]')?.select();
    tell('Could not copy it for you — the link is selected, copy it from there.', 'warn');
  };
  const copy = () => {
    if (link === null) return;
    if (!navigator.clipboard) return byHand();
    void navigator.clipboard.writeText(link).then(() => {
      setCopied(true);
      play('lock');
    }, byHand);
  };
  const nav = navigator as Navigator & {
    share?: (d: { title: string; text: string; url: string }) => Promise<void>;
  };
  const canShare = typeof nav.share === 'function';
  const share = () => {
    if (link === null || seat === null || !nav.share) return;
    // A dismissed share sheet rejects, and that is the person changing their mind.
    void nav
      .share({ title: view.league.name, text: `Take over ${seat.name}`, url: link })
      .catch(() => undefined);
  };

  return (
    <Sheet
      opened={teamId !== null}
      onClose={close}
      title={seat ? `A new manager for ${seat.name}` : 'A new manager'}
    >
      {seat === null ? null : seat.seat === 'manager' ? (
        <Text tone="secondary" block>
          {seat.ownerName} runs {seat.name} now.
        </Text>
      ) : (
        <Stack gap={3}>
          {inviteLines(seat).map((line) => (
            <Text key={line} tone="secondary" block>
              {line}
            </Text>
          ))}
          {link !== null ? (
            <>
              <Line>
                <TextField
                  label="Team invite link"
                  hideLabel
                  grow
                  readOnly
                  value={link}
                  onChange={() => {}}
                />
                <Hint label={copied ? 'Copied' : 'Copy the link'}>
                  <Button variant={copied ? 'soft' : 'secondary'} onClick={copy}>
                    {copied ? 'Copied' : 'Copy'}
                  </Button>
                </Hint>
                {canShare ? (
                  <Button icon="share" onClick={share} label="Share the team invite link">
                    Share
                  </Button>
                ) : null}
              </Line>
              <Line wrap>
                <Confirm
                  label="New link"
                  confirmLabel="Replace it?"
                  tone="warn"
                  busy={busy}
                  onConfirm={() => void run('invite', 'New link. The old one stops working.')}
                />
                <Confirm
                  label="Cancel the link"
                  confirmLabel="Cancel it?"
                  busy={busy}
                  onConfirm={() => void run('cancel-invite', 'Link canceled.')}
                />
              </Line>
            </>
          ) : (
            <Line wrap>
              <Button
                variant="primary"
                onClick={() => void run('invite', 'Link made. Send it to the new manager.')}
                loading={busy}
                disabled={busy}
              >
                Make an invite link
              </Button>
              <Button variant="quiet" onClick={close} disabled={busy}>
                Not now
              </Button>
            </Line>
          )}
          {problem !== null ? <Callout tone="bad">{problem}</Callout> : null}
        </Stack>
      )}
    </Sheet>
  );
}

/** What putting a bot on a team nobody runs does, before it is done (J3). */
export function botLines(seat: SeatRef): string[] {
  return [
    `A bot plays ${seat.name} until somebody takes it over: one the site picks, as it does at the draft, or one you choose.`,
    'Like every bot, it sets the lineup before each kickoff, picks up players and offers trades as often as its personality says, and answers trade offers and votes when a trade is up for review.',
    seat.invite !== null
      ? 'The invite link you made still works: whoever takes the team over, the bot stops.'
      : 'You can still invite somebody to take it over, and the bot stops then.',
    'The commissioner log says you did it.',
  ];
}

/** What taking the bot off does (J3). */
export function unbotLines(seat: SeatRef): string[] {
  return [
    `${seat.name} has nobody again: it plays its last lineup each week, and nobody answers the trade offers made to it.`,
    'The commissioner log says you did it.',
  ];
}

export function BotSheet({
  view,
  leagueId,
  teamId,
  onClose,
  onView,
}: {
  view: LeagueSetupView;
  leagueId: string;
  teamId: string | null;
  onClose: () => void;
  onView: (view: LeagueSetupView) => void;
}) {
  const seat = view.seats.find((s) => s.teamId === teamId) ?? null;
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  // THE CHOOSER, past the draft too (AI_MANAGERS.md §3.1, B-459): a
  // character and a level, where the button beside it is the house's pick.
  const [choosing, setChoosing] = useState(false);
  const close = () => {
    setProblem(null);
    onClose();
  };
  const run = async (act: 'bot' | 'unbot', done: string) => {
    if (seat === null) return;
    setBusy(true);
    setProblem(null);
    const reply = await seatAct(leagueId, seat.teamId, act);
    setBusy(false);
    if (reply.status !== 'ok') return setProblem(messageOf(reply, 'That did not work.', reached));
    onView(reply.data);
    tell(done, 'good');
    onClose();
  };
  // The seat as it is now: somebody may have taken it in the meantime.
  const playing = seat !== null && seat.seat === 'bot';
  return (
    <>
      <Sheet
        opened={teamId !== null && !choosing}
        onClose={close}
        title={
          seat === null
            ? 'A bot'
            : playing
              ? `${seat.botName ?? 'A bot'} plays ${seat.name}`
              : `A bot for ${seat.name}`
        }
      >
        {seat === null ? null : seat.seat === 'manager' ? (
          <Text tone="secondary" block>
            {seat.ownerName} runs {seat.name} now.
          </Text>
        ) : (
          <Stack gap={3}>
            {(playing ? unbotLines(seat) : botLines(seat)).map((line) => (
              <Text key={line} tone="secondary" block>
                {line}
              </Text>
            ))}
            {problem !== null ? <Callout tone="bad">{problem}</Callout> : null}
            {playing ? (
              <Line wrap>
                <Confirm
                  label="Take the bot off"
                  confirmLabel="Take it off?"
                  tone="warn"
                  busy={busy}
                  onConfirm={() =>
                    void run('unbot', `${seat.botName ?? 'The bot'} is off ${seat.name}.`)
                  }
                />
                <Button variant="quiet" onClick={close} disabled={busy}>
                  Keep it
                </Button>
              </Line>
            ) : (
              <Line wrap>
                <Button
                  variant="primary"
                  onClick={() => void run('bot', `A bot plays ${seat.name} now.`)}
                  loading={busy}
                  disabled={busy}
                >
                  Put a bot on it
                </Button>
                <Button onClick={() => setChoosing(true)} disabled={busy}>
                  Choose the bot
                </Button>
                <Button variant="quiet" onClick={close} disabled={busy}>
                  Not now
                </Button>
              </Line>
            )}
          </Stack>
        )}
      </Sheet>
      <BotSeatSheet
        leagueId={leagueId}
        teamId={choosing ? teamId : null}
        drafted
        onClose={() => setChoosing(false)}
        onView={(v) => {
          setChoosing(false);
          onView(v);
          onClose();
        }}
      />
    </>
  );
}

/**
 * The commissioner's nudge about one team's lineup (J3): what cannot score,
 * what the manager will get, and once a week. No reason is asked and nothing
 * is logged, because nothing in the league changes.
 */
export function NudgeSheet({
  seat,
  glance,
  period,
  leagueId,
  onClose,
  onGlance,
}: {
  seat: SeatRef | null;
  glance: LineupGlance | null;
  /** "Week 5". */
  period: string | null;
  leagueId: string;
  onClose: () => void;
  onGlance: (view: LeagueLineupsView) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const close = () => {
    setProblem(null);
    onClose();
  };
  const week = period ?? 'this week';
  const who = seat?.ownerName ?? 'The manager';
  // THE SLOTS, as the notification counts them: a roster's own problems (a
  // player on IR who has to come off, a roster over a limit) cost no points
  // this week (D9).
  const count =
    glance?.health.problems.filter(
      (p) => p.code !== 'reserve-illegal' && p.code !== 'roster-illegal',
    ).length ?? 0;
  const send = async () => {
    if (seat === null) return;
    setBusy(true);
    setProblem(null);
    const reply = await nudgeLineup(leagueId, seat.teamId);
    setBusy(false);
    if (reply.status !== 'ok') return setProblem(messageOf(reply, 'That did not work.', reached));
    onGlance(reply.data);
    tell(`Nudge sent to ${who}.`, 'good');
    onClose();
  };
  return (
    <Sheet opened={seat !== null} onClose={close} title={seat ? `Nudge ${who}` : 'Nudge'}>
      {seat === null || glance === null ? null : (
        <Stack gap={3}>
          <Callout tone={glance.health.status === 'error' ? 'warn' : 'neutral'}>
            <Stack gap={1}>
              <Text weight="strong" block>
                {seat.name}, {week}: {glance.health.headline}
              </Text>
              {glance.health.problems.map((p) => (
                <Text key={`${p.rowSlot}-${p.rowSlotIndex}-${p.code}`} block>
                  {p.message}
                </Text>
              ))}
            </Stack>
          </Callout>
          {glance.nudgedAt !== null ? (
            <Text tone="secondary" block>
              {who} had a nudge about {week} already. One a week is the most anybody gets.
            </Text>
          ) : glance.canNudge ? (
            <>
              <Text tone="secondary" block>
                {who} gets one notification from you: {count} slot{count === 1 ? '' : 's'} in{' '}
                {seat.name} can’t score in {week}. Nothing else happens, and nobody else is told.
              </Text>
              {problem !== null ? <Callout tone="bad">{problem}</Callout> : null}
              <Line wrap>
                <Button
                  variant="primary"
                  onClick={() => void send()}
                  loading={busy}
                  disabled={busy}
                >
                  Send the nudge
                </Button>
                <Button variant="quiet" onClick={close} disabled={busy}>
                  Not now
                </Button>
              </Line>
            </>
          ) : (
            <Text tone="secondary" block>
              There is nothing to nudge {who} about.
            </Text>
          )}
        </Stack>
      )}
    </Sheet>
  );
}
